/**
 * <s-wish-button data-handle>
 * Heart toggle for the browser-saved wishlist. The list is an array of product
 * handles in localStorage 'ma-wishlist' (newest first). Every change dispatches
 * 'wishlist:change' on document so all hearts, the header counter and the
 * wishlist drawer (s-wishlist) stay in sync, also across tabs via 'storage'.
 */
const KEY = 'ma-wishlist';

export function readWishlist() {
  try {
    const list = JSON.parse(localStorage.getItem(KEY) || '[]');
    return Array.isArray(list) ? list.filter((h) => typeof h === 'string') : [];
  } catch {
    return [];
  }
}

export function writeWishlist(list) {
  try {
    localStorage.setItem(KEY, JSON.stringify(list.slice(0, 60)));
  } catch {
    /* storage full or blocked: keep working for this page view */
  }
  document.dispatchEvent(new CustomEvent('wishlist:change'));
}

class SWishButton extends HTMLElement {
  #ac = null;

  connectedCallback() {
    this.#ac = new AbortController();
    const { signal } = this.#ac;
    const button = this.querySelector('button');
    if (!button) return;

    button.addEventListener('click', (event) => {
      event.preventDefault();
      event.stopPropagation();
      const handle = this.dataset.handle;
      const list = readWishlist();
      const adding = !list.includes(handle);
      const next = adding ? [handle, ...list] : list.filter((h) => h !== handle);
      writeWishlist(next);
      this.#feedback(button, adding);
    }, { signal });

    document.addEventListener('wishlist:change', () => this.#render(), { signal });
    window.addEventListener('storage', (event) => {
      if (event.key === KEY) this.#render();
    }, { signal });
    this.#render();
  }

  disconnectedCallback() {
    this.#ac?.abort();
  }

  /** Saving: ring + sparks on this heart, header counter bump. Both ways: a short note (toast). */
  #feedback(button, adding) {
    if (adding) {
      button.classList.remove('is-wish-burst');
      void button.offsetWidth; // restart the animation on quick repeat clicks
      button.classList.add('is-wish-burst');
      setTimeout(() => button.classList.remove('is-wish-burst'), 700);
      document.querySelectorAll('[data-wishlist-count]').forEach((counter) => {
        counter.classList.remove('wish-count-bump');
        void counter.offsetWidth;
        counter.classList.add('wish-count-bump');
      });
    }
    const message = adding ? this.dataset.messageAdded : this.dataset.messageRemoved;
    if (message) {
      document.dispatchEvent(new CustomEvent('s-notification:add', {
        detail: { message, variant: adding ? 'success' : 'info', duration: 2500 },
      }));
    }
  }

  #render() {
    const button = this.querySelector('button');
    if (!button) return;
    const saved = readWishlist().includes(this.dataset.handle);
    button.setAttribute('aria-pressed', String(saved));
    button.setAttribute('aria-label', saved ? this.dataset.labelRemove : this.dataset.labelAdd);
  }
}

if (!customElements.get('s-wish-button')) customElements.define('s-wish-button', SWishButton);
